/**
 * PATH: apps/qa/src/pages/new-inspection/useQuoteSource.ts
 * FILE_ID: QA-NEWINSPECTION-QUOTE-SOURCE-HOOK-001
 * PURPOSE: Own the wizard's optional quote source for an inspection.
 * WHY: The job hub already generates QA from quote scopes; the QA hub reuses
 *      that same server contract instead of re-deriving floors and areas.
 * FLOW: selected job -> quote sheets -> selection -> plan preview -> generate.
 */

import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  createQaApi,
  provenanceFromPreview,
  quoteSheetLabel,
  type QaCreateMode,
  type QaInspectionSummary,
  type QuoteSheetOption,
} from '@quotech/job-hub/jobs/qa'

import { api } from '@/lib/api'
import type { Job } from './inspection-types'

// The QA auth store keeps the organisation id as a string; the job hub adapter
// wants a number, so the coercion lives here rather than at every call site.
export function useQuoteSource(
  selectedJob: Job | null,
  organisationId?: string | number | null,
) {
  const jobId = selectedJob?.job_id ?? null
  const qaApi = useMemo(
    () =>
      createQaApi(
        // The QA hub client is the same axios instance shape the job hub
        // adapter expects, minus axios' generic response typing.
        api() as unknown as Parameters<typeof createQaApi>[0],
        organisationId != null ? Number(organisationId) : undefined,
      ),
    [organisationId],
  )

  const [selectedSheetIds, setSelectedSheetIds] = useState<number[]>([])
  const [createMode, setCreateMode] = useState<QaCreateMode>('combine')

  const sheetsQuery = useQuery({
    queryKey: ['qa-wizard-quote-sheets', jobId],
    queryFn: () => qaApi.listQuoteSheets(Number(jobId)),
    enabled: jobId != null,
  })

  // Every sheet is selectable; keep the readySheets name for callers.
  const sheets: QuoteSheetOption[] = sheetsQuery.data ?? []
  const readySheets = sheets

  const previewQuery = useQuery({
    queryKey: ['qa-wizard-plan-preview', selectedSheetIds.join(','), createMode],
    queryFn: () => qaApi.previewQaPlan(selectedSheetIds, createMode),
    enabled: selectedSheetIds.length > 0,
    retry: false,
  })

  const toggleSheet = useCallback((id: number) => {
    setSelectedSheetIds((current) =>
      current.includes(id) ? current.filter((sheetId) => sheetId !== id) : [...current, id],
    )
  }, [])

  const clearSelection = useCallback(() => setSelectedSheetIds([]), [])

  /** Create the inspection from the selected scopes. Only ever called when
   *  at least one sheet is selected; the blank wizard path is untouched. */
  const createFromQuotes = useCallback(
    async (inspectionName: string): Promise<QaInspectionSummary> => {
      const preview = previewQuery.data
      if (!preview || preview.plans.length === 0) {
        throw new Error('The quote scope preview has not loaded yet. Review it and try again.')
      }

      const idsFor = (plan: (typeof preview.plans)[number]) =>
        [...new Set(plan.sources.map((source) => source.quote_sheet_id))]
          .filter((id) => selectedSheetIds.includes(id))

      const withDup = (plan: (typeof preview.plans)[number]) => ({
        ...provenanceFromPreview(plan),
        allow_duplicate_source: true as const,
      })

      const name = inspectionName.trim()

      if (createMode === 'separate' && preview.plans.length > 1) {
        let first: QaInspectionSummary | null = null
        for (let index = 0; index < preview.plans.length; index++) {
          const plan = preview.plans[index]
          const ids = idsFor(plan)
          const provenance = withDup(plan)
          const inspection =
            ids.length === 1
              ? await qaApi.generateFromQuoteSheet(ids[0], undefined, provenance)
              : await qaApi.generateFromQuoteSheets(ids, undefined, provenance)
          if (name) {
            try {
              await qaApi.renameInspection(inspection.id, `${name} ${index + 1}`)
            } catch {
              /* keep the generated scope name rather than failing the create */
            }
          }
          if (!first) first = inspection
        }
        if (!first) {
          throw new Error('No inspections were created from the selected quotes.')
        }
        return first
      }

      const plan = preview.plans[0]
      const ids = idsFor(plan)
      const provenance = withDup(plan)
      const inspection =
        ids.length === 1
          ? await qaApi.generateFromQuoteSheet(ids[0], undefined, provenance)
          : await qaApi.generateFromQuoteSheets(ids, undefined, provenance)
      if (name) {
        try {
          await qaApi.renameInspection(inspection.id, name)
        } catch {
          /* keep the generated scope name rather than failing the create */
        }
      }
      return inspection
    },
    [createMode, previewQuery.data, qaApi, selectedSheetIds],
  )

  return {
    clearSelection,
    createFromQuotes,
    createMode,
    hasQuoteSource: selectedSheetIds.length > 0,
    label: quoteSheetLabel,
    preview: previewQuery.data,
    previewError: previewQuery.error,
    previewLoading: previewQuery.isFetching,
    readySheets,
    selectedSheetIds,
    setCreateMode,
    sheets,
    sheetsError: sheetsQuery.error,
    sheetsLoading: sheetsQuery.isLoading,
    toggleSheet,
  }
}

export type QuoteSourceController = ReturnType<typeof useQuoteSource>
